import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Card, CardBody, CompetitionPoster, PosterImage } from '@structural-climbing/ui'
import type { CompetitionSummary } from './types'
import { formatWallClock } from './wallClock'

/**
 * A competition as it appears in a list beside others.
 *
 * Built the same way as the card on the public site: the picture leads, the
 * state and whether you are in it sit on top of it, and everything written
 * follows underneath. A competition is an event, and an event announces itself
 * with a poster rather than with a row of text.
 */
export function CompetitionCard({ competition }: { competition: CompetitionSummary }) {
  const { t, i18n } = useTranslation()
  const live = competition.state === 'in_progress'
  const ended = competition.state === 'ended'

  const from = formatWallClock(competition.starts_at, i18n.language)
  // A single-day competition reads as one date; anything longer needs both ends.
  const sameDay = competition.starts_at.slice(0, 10) === competition.ends_at.slice(0, 10)
  const when = sameDay
    ? from
    : t('competitions.dateRange', {
        from,
        to: formatWallClock(competition.ends_at, i18n.language),
      })

  return (
    <Link to={`/competitions/${competition.slug}`} className="group block">
      <Card
        className={`overflow-hidden shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-md active:scale-[0.99] ${
          ended ? 'opacity-75' : ''
        }`}
      >
        <Poster
          competitionName={competition.name}
          gymName={competition.gym_name}
          posterUrl={competition.poster_url}
          gymImageUrl={competition.gym_image_url}
          badges={
            <>
              {live ? (
                <Badge className="bg-success text-white">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  {t('competitions.liveNow')}
                </Badge>
              ) : (
                <Badge className={ended ? 'bg-black/55 text-white' : 'bg-white/90 text-foreground'}>
                  {t(`competitions.state_${competition.state}`)}
                </Badge>
              )}
              {competition.joined && (
                <Badge className="bg-success/90 text-white">{t('competitions.joinedBadge')}</Badge>
              )}
            </>
          }
        />

        <CardBody className="gap-1 px-4 py-3">
          <h3 className="truncate font-bold leading-snug">{competition.name}</h3>
          <p className="truncate text-xs text-foreground/50">
            {competition.gym_name} · {when}
          </p>
          {/* The nudge, which is where the page gets its voice. */}
          <p className="mt-0.5 truncate text-sm text-foreground/70">
            {ended
              ? competition.joined
                ? t('competitions.cardSeeYourResult')
                : t('competitions.cardSeeResults')
              : competition.joined
                ? t('competitions.teaserJoined')
                : t('competitions.teaserJoin')}
          </p>
        </CardBody>
      </Card>
    </Link>
  )
}

function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm ${className}`}
    >
      {children}
    </span>
  )
}

/**
 * The picture frame: the gym's poster if it uploaded one, otherwise one drawn
 * from the competition's own name so no two cards look alike.
 *
 * 16:9 rather than the site's 4:3 because this list is one column on a phone,
 * where a 4:3 banner would push the next competition off the screen.
 */
function Poster({
  competitionName,
  gymName,
  posterUrl,
  gymImageUrl,
  badges,
}: {
  competitionName: string
  gymName: string | null
  posterUrl: string | null
  gymImageUrl: string | null
  badges: React.ReactNode
}) {
  return (
    <div className="relative aspect-[16/9] w-full overflow-hidden">
      {posterUrl ? (
        // Fitted, never cropped: a poster's name and date sit at its edges.
        <PosterImage src={posterUrl} />
      ) : (
        <CompetitionPoster name={competitionName} />
      )}
      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">{badges}</div>
      {/* The gym's own mark stays on the picture, so a poster never costs the
          venue its identity. */}
      {gymImageUrl && (
        <img
          src={gymImageUrl}
          alt={gymName ?? ''}
          className="absolute right-3 bottom-3 h-9 w-9 rounded-lg bg-white/90 object-contain p-1 shadow-sm"
        />
      )}
    </div>
  )
}
